import { useEffect, useState } from "react";
import api, { fmtIDR, fmtDate } from "@/lib/api";
import { PageHeader, Loading, Empty } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Search, Phone } from "lucide-react";

export default function Suppliers() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", address: "", category: "" });
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);

  const load = () => { setLoading(true); api.get("/suppliers").then(({ data }) => setRows(data)).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name) return toast.error("Nama supplier wajib");
    try { await api.post("/suppliers", form); toast.success("Supplier tersimpan"); setOpen(false); setForm({ name: "", phone: "", address: "", category: "" }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };
  const del = async (r) => { if (!window.confirm("Hapus supplier?")) return; await api.delete(`/suppliers/${r.id}`); load(); };

  const doSearch = async () => {
    if (!q.trim()) { setResults(null); return; }
    setSearching(true);
    try { const { data } = await api.get("/suppliers/search", { params: { q } }); setResults(data); }
    catch { toast.error("Gagal mencari"); } finally { setSearching(false); }
  };

  const waLink = (phone) => { let p = (phone || "").replace(/[^0-9]/g, ""); if (p.startsWith("0")) p = "62" + p.slice(1); return p ? `https://wa.me/${p}` : null; };

  return (
    <div>
      <PageHeader title="Database Supplier" subtitle="Data pemasok & pencarian supplier berdasarkan tipe unit">
        <Button onClick={() => setOpen(true)} className="bg-sky-600 hover:bg-sky-700" data-testid="supplier-add-btn"><Plus className="w-4 h-4 mr-1.5" /> Supplier Baru</Button>
      </PageHeader>

      {/* Pencarian supplier berdasarkan tipe iPhone/unit */}
      <div className="bg-sky-50 rounded-xl border border-sky-100 p-4 mb-4">
        <Label className="text-xs text-slate-600 font-medium">Cari Supplier berdasarkan Tipe Unit (mis. "iPhone 13")</Label>
        <div className="flex gap-2 mt-1.5 max-w-lg">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && doSearch()} placeholder="iPhone 13, Samsung A54, ..." className="pl-9 bg-white" data-testid="supplier-search-input" />
          </div>
          <Button onClick={doSearch} disabled={searching} className="bg-sky-600 hover:bg-sky-700" data-testid="supplier-search-btn">{searching ? "..." : "Cari"}</Button>
        </div>
        {results && (
          <div className="mt-3 bg-white rounded-lg border border-slate-200 overflow-x-auto" data-testid="supplier-search-results">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200"><tr>
                {["Supplier", "Unit Cocok", "Harga Beli Terakhir", "Tgl Terakhir", "Riwayat"].map((h) => <th key={h} className="px-3 py-2 text-left font-medium text-slate-500">{h}</th>)}
              </tr></thead>
              <tbody className="divide-y divide-slate-100">
                {results.length === 0 ? <tr><td colSpan={5}><Empty text="Belum ada riwayat supplier untuk unit ini" /></td></tr>
                  : results.map((r, i) => (
                    <tr key={i}>
                      <td className="px-3 py-2 font-medium">{r.supplier_name}</td>
                      <td className="px-3 py-2 text-slate-500">{r.matched_item}</td>
                      <td className="px-3 py-2 font-mono-num text-sky-700">{fmtIDR(r.last_price)}</td>
                      <td className="px-3 py-2 text-slate-500">{r.last_date ? fmtDate(r.last_date) : "-"}</td>
                      <td className="px-3 py-2">{r.count}x beli</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200"><tr>
            {["Nama", "No. WA", "Alamat", "Kategori Produk", "Aksi"].map((h) => <th key={h} className="px-4 py-3 text-left font-medium text-slate-500">{h}</th>)}
          </tr></thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? <tr><td colSpan={5}><Loading /></td></tr>
              : rows.length === 0 ? <tr><td colSpan={5}><Empty /></td></tr>
              : rows.map((r) => (
                <tr key={r.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 font-medium">{r.name}</td>
                  <td className="px-4 py-3">
                    {r.phone ? <a href={waLink(r.phone)} target="_blank" rel="noopener noreferrer" className="text-green-600 inline-flex items-center gap-1"><Phone className="w-3.5 h-3.5" />{r.phone}</a> : "-"}
                  </td>
                  <td className="px-4 py-3 text-slate-500 max-w-[220px] truncate">{r.address || "-"}</td>
                  <td className="px-4 py-3">{r.category || "-"}</td>
                  <td className="px-4 py-3 text-right"><button onClick={() => del(r)} className="p-1.5 text-slate-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button></td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Supplier Baru</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-2">
            <div className="col-span-2"><Label className="text-xs text-slate-500">Nama Supplier</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" data-testid="supplier-name" /></div>
            <div><Label className="text-xs text-slate-500">Nomor WA</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1" placeholder="08xxx" data-testid="supplier-phone" /></div>
            <div><Label className="text-xs text-slate-500">Kategori Produk</Label><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-1" placeholder="iPhone, Android, Aksesoris" data-testid="supplier-category" /></div>
            <div className="col-span-2"><Label className="text-xs text-slate-500">Alamat</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1" data-testid="supplier-address" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Batal</Button><Button onClick={create} className="bg-sky-600 hover:bg-sky-700" data-testid="supplier-save-btn">Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
